/**
 * @fileoverview Admin Notification Provider
 * 
 * Handles Firebase Cloud Messaging initialization and notification permissions.
 * For iOS Safari PWA, uses Web Push API instead of FCM.
 * Uses useAdminOrderStore for state management.
 * 
 * @module context/AdminNotificationProvider
 */

import React, { useEffect, useRef, useCallback, useState } from "react";
import { messaging } from "../firebase";
import { getToken, onMessage } from "firebase/messaging";
import { addDeviceToken, subscribeWebPush, urlBase64ToUint8Array } from "@/api";
import { useRouter } from '@/lib/navigation';
import { useRole } from "@/stores";
import { useAdminOrderStore } from "@/stores/useAdminOrderStore";
import { useNotificationCenterStore } from "@/stores/useNotificationCenterStore";
import { formatTime } from '@/utils/datetime';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';
import { useWebSocket } from '@/services/websocket/useWebSocket';

// VAPID public key for Web Push (iOS Safari)
const VAPID_PUBLIC_KEY = 'BMSiH8gMtu-cof0mkSUptWJDQJDSqh5vxqJHzpWCsQXdousmDBxnJMQ9NnPHcPHhTxJumKqu_gquJuQmiE-BvZA';

// Debug logger - stores logs for display
const debugLogs = [];
const addDebugLog = (message, type = 'info') => {
  const timestamp = formatTime(new Date(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const log = `[${timestamp}] ${message}`;
  debugLogs.push({ log, type });
  // Keep only last 50 logs
  if (debugLogs.length > 50) debugLogs.shift();
  // Dispatch event to update UI
  window.dispatchEvent(new CustomEvent('debug-log', { detail: { log, type } }));
};

// Helper function to register token with the server
const registerTokenWithServer = async (token, restaurant_id) => {
  try {
    addDebugLog(`Registering token with server for restaurant ${restaurant_id}`);
    const response = await addDeviceToken(restaurant_id, token, navigator.userAgent);
    if (typeof response === 'object') {
      addDebugLog(`Token registered successfully`);
      return response;
    }
    if (response instanceof Response) return await response.json();
    throw new Error('Unexpected response format');
  } catch (error) {
    addDebugLog(`Token registration error: ${error.message}`, 'error');
    throw error;
  }
};

// Helper function to subscribe to Web Push (for iOS Safari PWA)
const subscribeToWebPush = async (restaurant_id) => {
  try {
    addDebugLog(`Subscribing to Web Push for restaurant ${restaurant_id}...`);
    
    const registration = await navigator.serviceWorker.ready;
    addDebugLog(`SW ready for Web Push: ${registration.scope}`);
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      addDebugLog('Already have Web Push subscription, updating server...');
    } else {
      // Subscribe to push notifications
      addDebugLog('Creating new Web Push subscription...');
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });
      addDebugLog('Web Push subscription created!');
    }
    
    // Send subscription to server
    const response = await subscribeWebPush(restaurant_id, subscription);
    addDebugLog(`Web Push subscription registered: ${response.message}`);
    
    return subscription;
  } catch (error) {
    addDebugLog(`Web Push subscription error: ${error.message}`, 'error');
    throw error;
  }
};

// iOS detection helpers
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
};

/**
 * Admin Notification Provider
 * 
 * Initializes Firebase messaging, handles notification permissions,
 * and sets up WebSocket connections for real-time order updates.
 */
export const AdminNotificationProvider = ({ children }) => {
  const { restaurant } = useRole();
  const router = useRouter();
  const { t } = useTranslation();
  const notificationSoundRef = useRef(null);
  const initialFetchRef = useRef(false);

  // Get store actions and state
  const {
    setFcmToken,
    setNotificationPermission,
    setTokenRegistered,
    userInteracted,
    fetchOrders,
    initialize,
    initializeWebSocket,
  } = useAdminOrderStore();

  const notificationPermission = useAdminOrderStore((s) => s.notificationPermission);

  // Function to handle token retrieval and registration
  const handleTokenRegistration = useCallback(async (registration) => {
    try {
      addDebugLog(`Getting FCM token with VAPID key...`);
      const currentToken = await getToken(messaging, {
        vapidKey: 'BNpGB4TteAjHw_nZt5M655XjFGbDRvMbNkDeR1bPSNC4XuZu_0NDkuPLwWfe0JHUQqllgwqsiCITdAaYPD2Wijs',
        serviceWorkerRegistration: registration
      });

      addDebugLog(`FCM Token received: ${currentToken ? currentToken.substring(0, 20) + '...' : 'null'}`);

      if (currentToken && restaurant?.id) {
        localStorage.setItem('fcmToken', currentToken);
        localStorage.setItem('fcmRestaurantId', restaurant.id);

        await registerTokenWithServer(currentToken, restaurant.id);
        setFcmToken(currentToken);
        setTokenRegistered(true);
        addDebugLog(`Token registration complete!`);
      } else {
        addDebugLog(`No token or restaurant ID`, 'error');
      }
    } catch (error) {
      addDebugLog(`Token error: ${error.message}`, 'error');
    }
  }, [restaurant, setFcmToken, setTokenRegistered]);

  // Initialize Firebase messaging and register token when restaurant changes
  useEffect(() => {
    const initNotifications = async () => {
      try {
        const iosDevice = isIOS();
        const pwaMode = isStandalone();
        const hasNotificationAPI = 'Notification' in window;
        const hasPushManager = 'PushManager' in window;
        
        addDebugLog(`Init Notifications - iOS: ${iosDevice}, PWA: ${pwaMode}`);
        addDebugLog(`Notification API: ${hasNotificationAPI}, PushManager: ${hasPushManager}`);

        // iOS Safari (not installed as PWA) - explain to user
        if (iosDevice && !pwaMode) {
          addDebugLog('❌ iOS Safari detected - Notifications require PWA mode!', 'error');
          addDebugLog('👉 Tap Share button (📤) → "Add to Home Screen" → Open from icon', 'warn');
          setNotificationPermission('default');
          return;
        }

        // No Notification API
        if (!hasNotificationAPI) {
          addDebugLog('Notification API not available', 'error');
          return;
        }

        // Check current permission
        const currentPermission = Notification.permission;
        addDebugLog(`Current permission: ${currentPermission}`);
        setNotificationPermission(currentPermission);

        // iOS PWA - Use Web Push API (not FCM)
        if (iosDevice && pwaMode) {
          addDebugLog('📱 iOS PWA detected - using Web Push API');
          
          if (currentPermission === 'granted') {
            addDebugLog('Permission granted, subscribing to Web Push...');
            await subscribeToWebPush(restaurant.id);
            setTokenRegistered(true);
          } else if (currentPermission === 'default') {
            addDebugLog('iOS PWA - waiting for user gesture to request permission');
          }
          return;
        }

        // Non-iOS: Use Firebase Cloud Messaging
        if (!messaging) {
          addDebugLog('Firebase messaging not initialized', 'error');
          return;
        }

        // If permission already granted, register FCM token
        if (currentPermission === 'granted') {
          addDebugLog('Permission granted, getting FCM token...');
          const registration = await navigator.serviceWorker.ready;
          addDebugLog(`SW ready: ${registration.scope}`);
          messaging.swRegistration = registration;
          await handleTokenRegistration(registration);
          return;
        }

        // If permission is default (not asked yet) - auto request for non-iOS
        if (currentPermission === 'default') {
          addDebugLog('Auto-requesting permission (non-iOS)...');
          const permission = await Notification.requestPermission();
          addDebugLog(`Permission result: ${permission}`);
          setNotificationPermission(permission);
          if (permission === 'granted') {
            const registration = await navigator.serviceWorker.ready;
            messaging.swRegistration = registration;
            await handleTokenRegistration(registration);
          }
        }
      } catch (error) {
        addDebugLog(`Notification init error: ${error.message}`, 'error');
      }
    };

    if (restaurant?.id) {
      addDebugLog(`Restaurant ID: ${restaurant.id}`);
      initNotifications();
    }
  }, [restaurant, handleTokenRegistration, setNotificationPermission]);

  // Initialize notification sound
  useEffect(() => {
    notificationSoundRef.current = new Audio("/sounds/notification.mp3");
    notificationSoundRef.current.preload = "auto";
    notificationSoundRef.current.onerror = (e) => console.error("Sound error:", e);
    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.pause();
        notificationSoundRef.current = null;
      }
    };
  }, []);

  // Initial orders fetch and WebSocket setup
  useEffect(() => {
    if (restaurant?.id && !initialFetchRef.current) {
      initialize(router, restaurant);
      initialFetchRef.current = true;
    }
  }, [restaurant, router, initialize]);

  // Re-initialize WebSocket when the RESTAURANT changes — never on a page change. `router` is a new
  // object at every navigation: depending on it re-joined the room and re-fetched print/capabilities +
  // print/jobs on every page opened (QA 13/10 C2: ~60 calls in 6 min among the busiest routes).
  const routerRef = useRef(router);
  routerRef.current = router;
  const restaurantRef = useRef(restaurant);
  restaurantRef.current = restaurant;
  const wsRestaurantIdRef = useRef(null);
  useEffect(() => {
    const id = restaurant?.id;
    if (!id || !initialFetchRef.current) return;
    if (wsRestaurantIdRef.current === null) { wsRestaurantIdRef.current = id; return; }   // initialize() just did it
    if (wsRestaurantIdRef.current === id) return;
    wsRestaurantIdRef.current = id;
    initializeWebSocket(routerRef.current, restaurantRef.current);
  }, [restaurant?.id, initializeWebSocket]);

  // Realtime alerts shown wherever the manager is in the dashboard.
  useWebSocket(
    restaurant?.id ? `admin-restaurant:${restaurant.id}` : '',
    {
      'haccp::temperature_alert': (data) => {
        const temperature = Number(data?.temperature_c);
        const text = t('Temperature alert: {{equipment}} at {{temperature}} °C', {
          equipment: data?.equipment || '',
          temperature: Number.isFinite(temperature) ? temperature.toFixed(1) : '?',
        });
        const message = data?.advice ? `${text} — ${data.advice}` : text;
        if (data?.level === 'severe') toast.error(message, { duration: 12000 });
        else toast.warning(message, { duration: 8000 });
      },
      'order::refunded': (data) => {
        if (!data?.orderId) return;
        toast.info(t('Order #{{id}} refunded', { id: data.orderId }));
      },
    },
    { enabled: Boolean(restaurant?.id), deps: [restaurant?.id] }
  );

  // Setup Firebase message listener (FCM - for non-iOS only)
  useEffect(() => {
    // Skip FCM listener on iOS - we use Web Push instead
    if (isIOS()) {
      addDebugLog('iOS detected - skipping FCM onMessage listener (using Web Push)');
      return;
    }
    
    if (!messaging) return;

    addDebugLog('Setting up FCM onMessage listener');
    
    const unsubscribe = onMessage(messaging, (payload) => {
      addDebugLog(`Foreground FCM message received: ${JSON.stringify(payload.data || payload.notification)}`);

      // Something landed in the feed: refresh the bell badge now.
      useNotificationCenterStore.getState().onRealtimeEvent();

      // Play the notification sound if the user has interacted. A service worker
      // cannot play audio, so this is the ONLY place the sound can fire while the
      // dashboard is in the foreground.
      if (document.visibilityState === 'visible' && userInteracted && notificationSoundRef.current) {
        notificationSoundRef.current.play().catch(e => addDebugLog(`Sound error: ${e}`, 'error'));
      }

      // The notification banner itself is displayed by the service worker's
      // 'push' handler (the single source of truth) — it fires even in the
      // foreground. Showing it here too would produce a duplicate, so we do NOT
      // call showNotification here.
    });

    return () => unsubscribe();
  }, [notificationPermission, userInteracted]);

  // Every push (FCM or Web Push, iOS included) goes through the service worker,
  // which tells the open pages: refresh the bell badge without waiting for the poll.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const onSwMessage = (event) => {
      if (event?.data?.type === 'ods-push') useNotificationCenterStore.getState().onRealtimeEvent();
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, []);

  // Store the requestNotificationPermission function in the store
  useEffect(() => {
    const requestNotificationPermission = async () => {
      addDebugLog('requestNotificationPermission called');
      if (!restaurant?.id) {
        addDebugLog('No restaurant ID', 'error');
        return;
      }
      
      // iOS in browser - show helpful message
      if (isIOS() && !isStandalone()) {
        addDebugLog('iOS Safari - need PWA for notifications', 'warn');
        return 'ios-pwa-required';
      }

      try {
        addDebugLog('Requesting notification permission...');
        const permission = await Notification.requestPermission();
        addDebugLog(`Permission result: ${permission}`);
        setNotificationPermission(permission);
        
        if (permission === 'granted') {
          // iOS PWA - Use Web Push API
          if (isIOS() && isStandalone()) {
            addDebugLog('📱 iOS PWA - subscribing to Web Push...');
            await subscribeToWebPush(restaurant.id);
            setTokenRegistered(true);
            addDebugLog('✅ Web Push subscription complete!');
          } else {
            // Non-iOS - Use Firebase Cloud Messaging
            const registration = await navigator.serviceWorker.ready;
            addDebugLog('Getting FCM token after permission grant...');
            const currentToken = await getToken(messaging, {
              vapidKey: 'BNpGB4TteAjHw_nZt5M655XjFGbDRvMbNkDeR1bPSNC4XuZu_0NDkuPLwWfe0JHUQqllgwqsiCITdAaYPD2Wijs',
              serviceWorkerRegistration: registration
            });
            addDebugLog(`FCM Token: ${currentToken ? currentToken.substring(0, 20) + '...' : 'null'}`);
            if (currentToken) {
              localStorage.setItem('fcmToken', currentToken);
              localStorage.setItem('fcmRestaurantId', restaurant.id);

              await registerTokenWithServer(currentToken, restaurant.id);
              setFcmToken(currentToken);
              setTokenRegistered(true);
            }
          }
        }
        return permission;
      } catch (error) {
        addDebugLog(`Permission error: ${error.message}`, 'error');
      }
    };

    // Store the function in the store for access via useAdminOrder
    useAdminOrderStore.setState({ _requestNotificationPermission: requestNotificationPermission });
  }, [restaurant, setNotificationPermission, setFcmToken, setTokenRegistered]);

  return <>{children}</>;
};

// Re-export for backward compatibility - components should import from @/stores
export { useAdminOrder } from "@/stores/useAdminOrderStore";
